import type { ComponentType } from 'react';

import type { Actions, Subjects } from '@cast/shared-ability-data-access';
import { lazyRetry } from '@cast/utils';

import { HomePage } from '@oneclick/pages/home/HomePage';
import {
  BASE_PATHS,
  DBO_SEGMENTS,
  ORG_COST_REPORT_PATHS,
  OVERVIEW_ROUTE_SEGMENTS,
  SETTINGS_PATHS,
  buildCloudVmsPath,
  buildNetworkIntelligencePath,
  buildOverviewUrl,
} from '@oneclick/constants/navigation';

import type {
  BreadcrumbItem,
  OneClickBreadcrumbOnlyItem,
  OneClickLinkItem,
  OneClickNavItem,
} from '@oneclick/routing/types';
import { withReportingLimit } from '@oneclick/routing/reportingLimit';

type NavRules = Array<[Actions, Subjects]>;

export type OrgNavLeaf = {
  id: string;
  title: string;
  path: string;
  matchRoute?: string;
  end?: boolean;
  rules?: NavRules;
  breadcrumbs?: BreadcrumbItem[];
  segment?: string;
  icon?: string;
  basePath?: string;
  requiresClustersWithApa?: boolean;
  hiddenWhenReportingLimited?: boolean;
};

export const ORG_SIDEBAR_SECTION_TITLES = {
  fleet: 'Fleet',
  clusterSchedules: 'Cluster schedules',
  costMonitoring: 'Cost monitoring',
  costManagement: 'Cost management',
  organizationSettings: 'Organization settings',
  logs: 'Event logs',
} as const;

export const ORG_DBO_SECTION = {
  title: 'Databases',
  icon: 'databases',
  hiddenWhenReportingLimited: true,
} as const;

export const ORG_APA_SECTION = {
  title: 'APA',
  icon: 'apa',
  hiddenWhenReportingLimited: true,
} as const;

export const CLUSTERS_HOME_ROUTE_CONFIG: OneClickNavItem[] = [
  {
    id: 'clusters',
    title: 'Clusters',
    route: 'clusters',
    page: HomePage,
    matchRoute: BASE_PATHS.clusters,
    end: true,
    breadcrumbs: ({ search }) => {
      const view = new URLSearchParams(search).get('view');
      if (view === 'list') {
        return [
          {
            label: 'Cluster list',
            to: `${BASE_PATHS.clusters}?view=list`,
          },
        ];
      }
      return ['Clusters'];
    },
  },
];

export const getClustersHomePage = (): ComponentType => HomePage;

export const ORG_FLEET_ITEMS: OrgNavLeaf[] = [
  {
    id: 'clusters',
    title: 'Clusters',
    path: BASE_PATHS.clusters,
    matchRoute: BASE_PATHS.clusters,
    end: true,
    breadcrumbs: ['Clusters'],
  },
  {
    id: 'overview',
    title: 'Overview',
    path: buildOverviewUrl(OVERVIEW_ROUTE_SEGMENTS.organization),
    matchRoute: BASE_PATHS.overview,
    rules: [
      ['view', 'ClusterAutomationNewDashboard'],
      ['view', 'OrganizationCostMonitoring'],
    ],
    breadcrumbs: ['Overview'],
  },
  {
    id: 'cloudVms',
    title: 'Cloud VMs',
    path: buildCloudVmsPath(),
    matchRoute: BASE_PATHS.cloudVms,
    breadcrumbs: ['Cloud VMs'],
  },
];

export const ORG_DBO_CHILDREN: OrgNavLeaf[] = withReportingLimit([
  {
    id: 'caches',
    title: 'Caches',
    path: `${BASE_PATHS.dbo}/${DBO_SEGMENTS.caches}`,
    segment: DBO_SEGMENTS.caches,
    icon: 'dboCaches',
    rules: [['view', 'OrganizationDbOptimizer']],
    breadcrumbs: ['Caches'],
  },
  {
    id: 'cloudDatabases',
    title: 'Databases',
    path: `${BASE_PATHS.dbo}/${DBO_SEGMENTS.cloudDatabases}`,
    segment: DBO_SEGMENTS.cloudDatabases,
    icon: 'dboCloudDatabases',
    rules: [['view', 'OrganizationDbOptimizer']],
    breadcrumbs: ['Databases'],
  },
  {
    id: 'performanceAdvisor',
    title: 'Performance advisor',
    path: `${BASE_PATHS.dbo}/${DBO_SEGMENTS.performanceAdvisor}`,
    segment: DBO_SEGMENTS.performanceAdvisor,
    icon: 'dboPerformanceAdvisor',
    breadcrumbs: ['Performance advisor'],
  },
]);

export const ORG_APA_CHILDREN: OrgNavLeaf[] = withReportingLimit([
  {
    id: 'runbooks',
    title: 'Runbooks',
    path: `${BASE_PATHS.apa}/runbooks`,
    breadcrumbs: ['Runbooks'],
  },
  {
    id: 'integrations',
    title: 'Integrations',
    path: `${BASE_PATHS.apa}/integrations`,
    rules: [['view', 'OrganizationApaRunbooks']],
    breadcrumbs: ['Integrations'],
  },
  {
    id: 'settings',
    title: 'Settings',
    path: `${BASE_PATHS.apa}/settings`,
    rules: [['view', 'OrganizationApaRunbooks']],
    requiresClustersWithApa: true,
    breadcrumbs: ['Settings'],
  },
]);

export const ORG_EXTRA_FEATURES: OrgNavLeaf[] = withReportingLimit([
  {
    id: 'networkIntelligence',
    title: 'Network intelligence',
    path: buildNetworkIntelligencePath(),
    basePath: BASE_PATHS.networkIntelligence,
    matchRoute: BASE_PATHS.networkIntelligence,
    icon: 'networkIntelligence',
    rules: [['view', 'OrganizationNetworkIntelligence']],
    breadcrumbs: ['Network intelligence'],
  },
]);

export const ORG_COST_MONITORING_CHILDREN: OrgNavLeaf[] = [
  {
    id: 'clustersReport',
    title: 'Clusters',
    path: ORG_COST_REPORT_PATHS.clusters,
    rules: [['view', 'OrganizationCostMonitoring']],
    breadcrumbs: ['Clusters'],
  },
  {
    id: 'allocationGroupsReport',
    title: 'Allocation groups',
    path: ORG_COST_REPORT_PATHS.allocationGroups,
    rules: [['view', 'OrganizationCostMonitoring']],
    breadcrumbs: ['Allocation groups'],
  },
  {
    id: 'idleResourcesReport',
    title: 'Idle resources',
    path: ORG_COST_REPORT_PATHS.idleResources,
    rules: [['view', 'OrganizationCostMonitoring']],
    breadcrumbs: ['Idle resources'],
  },
  {
    id: 'nodeTemplatesReport',
    title: 'Node templates',
    path: ORG_COST_REPORT_PATHS.nodeTemplates,
    rules: [['view', 'OrganizationCostMonitoringNodeTemplates']],
    breadcrumbs: ['Node templates'],
    hiddenWhenReportingLimited: true,
  },
  {
    id: 'savingsReport',
    title: 'Savings report',
    path: ORG_COST_REPORT_PATHS.savingsReport,
    rules: [['view', 'ValueReport']],
    breadcrumbs: ['Savings report'],
    hiddenWhenReportingLimited: true,
  },
  {
    id: 'budgets',
    title: 'Budgets',
    path: ORG_COST_REPORT_PATHS.budgets,
    rules: [['view', 'OrganizationBudgets']],
    breadcrumbs: ['Budgets'],
    hiddenWhenReportingLimited: true,
  },
  {
    id: 'customDashboards',
    title: 'Custom dashboards',
    path: ORG_COST_REPORT_PATHS.customDashboards,
    rules: [['view', 'OrganizationCustomDashboards']],
    breadcrumbs: ['Custom dashboards'],
    hiddenWhenReportingLimited: true,
  },
];

export const ORG_COST_MANAGEMENT_CHILDREN: OrgNavLeaf[] = [
  {
    id: 'commitments',
    title: 'Commitments',
    path: SETTINGS_PATHS.commitments,
    rules: [['view', 'OrganizationCommitments']],
    breadcrumbs: ['Commitments'],
    hiddenWhenReportingLimited: true,
  },
  {
    id: 'priceAdjustments',
    title: 'Price adjustment',
    path: SETTINGS_PATHS.priceAdjustments,
    rules: [['view', 'OrganizationDiscounts']],
    breadcrumbs: ['Price adjustment'],
  },
];

export const ORG_SETTINGS_CHILDREN: OrgNavLeaf[] = [
  {
    id: 'overview',
    title: 'Overview',
    path: SETTINGS_PATHS.overview,
    breadcrumbs: ['Overview'],
  },
  {
    id: 'platformUsage',
    title: 'Platform usage',
    path: SETTINGS_PATHS.platformUsage,
    rules: [['view', 'OrganizationBillingReport']],
    breadcrumbs: ['Platform usage'],
  },
  {
    id: 'featureControl',
    title: 'Feature control',
    path: SETTINGS_PATHS.featureControl,
    rules: [['view', 'FeatureOrganizationManagementFeatureControl']],
    breadcrumbs: ['Feature control'],
  },
  {
    id: 'cloudIntegrations',
    title: 'Cloud integrations',
    path: SETTINGS_PATHS.cloudIntegrations,
    rules: [['view', 'OrganizationCloudIntegrations']],
    breadcrumbs: ['Cloud integrations'],
  },
  {
    id: 'componentControl',
    title: 'Component control',
    path: SETTINGS_PATHS.componentControl,
    rules: [['view', 'FeatureOrganizationManagementComponentControl']],
    breadcrumbs: ['Component control'],
    hiddenWhenReportingLimited: true,
  },
  {
    id: 'permissionsCenter',
    title: 'Permissions center',
    path: SETTINGS_PATHS.permissionsCenter,
    rules: [['view', 'FeatureOrganizationManagementRoleBindings']],
    breadcrumbs: ['Permissions center'],
    hiddenWhenReportingLimited: true,
  },
];

const CloudVmsController = lazyRetry(() =>
  import('@oneclick/pages/cloud-vms/CloudVmsController').then((m) => ({
    default: m.CloudVmsController,
  }))
);
const ApaController = lazyRetry(() =>
  import('@oneclick/pages/apa/ApaController').then((m) => ({
    default: m.ApaController,
  }))
);
const EventLogController = lazyRetry(
  () => import('@oneclick/pages/event-log/EventLogController')
);
const NotificationsController = lazyRetry(() =>
  import('@oneclick/pages/notifications/NotificationsController').then((m) => ({
    default: m.NotificationsController,
  }))
);
const NetworkIntelligenceController = lazyRetry(() =>
  import(
    '@oneclick/pages/network-intelligence/NetworkIntelligenceController'
  ).then((m) => ({ default: m.NetworkIntelligenceController }))
);
const EnterpriseController = lazyRetry(() =>
  import('@oneclick/pages/enterprise/EnterpriseController').then((m) => ({
    default: m.EnterpriseController,
  }))
);
const ApiAccessKeysController = lazyRetry(() =>
  import('@oneclick/pages/api-access-keys/ApiAccessKeysController').then(
    (m) => ({ default: m.ApiAccessKeysController })
  )
);
const OrganizationCostReportController = lazyRetry(
  () => import('@oneclick/pages/cost-report/CostReportController')
);

const ORG_SHELL_ROUTE_CONFIG: OneClickLinkItem[] = [
  {
    id: 'cloudVms',
    title: 'Cloud VMs',
    route: 'automation/cloud-vms/*',
    page: CloudVmsController,
    matchRoute: BASE_PATHS.cloudVms,
    breadcrumbs: ['Cloud VMs'],
  },
  {
    id: 'apa',
    title: 'APA',
    route: 'apa/*',
    page: ApaController,
    matchRoute: BASE_PATHS.apa,
    breadcrumbs: ['Runbooks'],
  },
  {
    id: 'eventLog',
    title: ORG_SIDEBAR_SECTION_TITLES.logs,
    route: 'activity/events/*',
    page: EventLogController,
    matchRoute: BASE_PATHS.eventLog,
    breadcrumbs: [ORG_SIDEBAR_SECTION_TITLES.logs],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    route: 'activity/notifications/*',
    page: NotificationsController,
    matchRoute: BASE_PATHS.notifications,
    breadcrumbs: ['Notifications'],
  },
  {
    id: 'networkIntelligence',
    title: 'Network intelligence',
    route: 'automation/network-intelligence/*',
    page: NetworkIntelligenceController,
    matchRoute: BASE_PATHS.networkIntelligence,
    breadcrumbs: ['Network intelligence'],
    rules: [['view', 'OrganizationNetworkIntelligence']],
  },
  {
    id: 'enterprise',
    title: 'Organization settings',
    route: 'organization/enterprise/:enterpriseId/*',
    page: EnterpriseController,
    matchRoute: '/organization/enterprise/:enterpriseId',
    breadcrumbs: ['Organization settings'],
  },
  {
    id: 'apiAccess',
    title: 'API keys',
    route: 'user/api-access/*',
    page: ApiAccessKeysController,
    matchRoute: BASE_PATHS.apiAccess,
    breadcrumbs: ['API keys'],
  },
  {
    id: 'costReport',
    title: 'Clusters',
    route: 'automation/cost-report/*',
    page: OrganizationCostReportController,
    matchRoute: BASE_PATHS.costReport,
    breadcrumbs: ['Clusters'],
  },
];

export const getOrgShellPage = (id: string): ComponentType => {
  const item = ORG_SHELL_ROUTE_CONFIG.find((leaf) => leaf.id === id);
  if (!item) {
    throw new Error(`Missing org shell route config id: ${id}`);
  }
  return item.page;
};

const leafToBreadcrumb = (leaf: OrgNavLeaf): OneClickBreadcrumbOnlyItem => {
  const labels = leaf.breadcrumbs ?? [leaf.title];
  return {
    matchRoute: leaf.matchRoute ?? leaf.path,
    end: leaf.end,
    breadcrumbs: labels.map((item) => {
      if (typeof item === 'string') {
        return { label: item, to: leaf.path };
      }
      return item.to ? item : { ...item, to: leaf.path };
    }),
  };
};

export const ORG_BREADCRUMB_ONLY: OneClickBreadcrumbOnlyItem[] = [
  ...ORG_FLEET_ITEMS.filter((item) => item.id !== 'clusters').map(
    leafToBreadcrumb
  ),
  ...ORG_EXTRA_FEATURES.map(leafToBreadcrumb),
  ...ORG_DBO_CHILDREN.map(leafToBreadcrumb),
  {
    matchRoute: `${BASE_PATHS.dbo}/overview`,
    breadcrumbs: [ORG_DBO_SECTION.title],
  },
  {
    matchRoute: BASE_PATHS.dbo,
    breadcrumbs: [ORG_DBO_SECTION.title],
  },
  ...ORG_APA_CHILDREN.map(leafToBreadcrumb),
  ...ORG_COST_MONITORING_CHILDREN.map(leafToBreadcrumb),
  ...ORG_COST_MANAGEMENT_CHILDREN.map(leafToBreadcrumb),
  ...ORG_SETTINGS_CHILDREN.filter(
    (item) => item.id !== 'overview' && item.id !== 'platformUsage'
  ).map(leafToBreadcrumb),
  ...ORG_SHELL_ROUTE_CONFIG.map((item) => ({
    matchRoute: item.matchRoute!,
    breadcrumbs:
      typeof item.breadcrumbs === 'function'
        ? item.breadcrumbs
        : item.breadcrumbs,
  })),
  {
    matchRoute: SETTINGS_PATHS.overview,
    breadcrumbs: ['Overview'],
  },
  {
    matchRoute: BASE_PATHS.settings,
    breadcrumbs: ['Overview'],
  },
  {
    matchRoute: BASE_PATHS.accessControl,
    breadcrumbs: ['Users'],
  },
  {
    matchRoute: BASE_PATHS.overview,
    breadcrumbs: ['Overview'],
  },
];
